import { secureGet, securePost, securePut } from '../utils/HttpUtils';

const apiUrls = {
  annexes: '/v1/private/annexes',
};

export interface AnnexItem {
  id?: number;
  name: string;
  code?: string;
  requires_year: boolean;
  requires_month: boolean;
  query: string;
  created_at?: string;
  updated_at?: string;
  casfog_endpoint?: string;
  casfog_enabled: boolean;
}

export interface SubmissionSummary {
  id: number;
  annexId: number;
  annexName?: string;
  annexCode?: string;
  year: number;
  month: number;
  submissionType: string;
  status: string;
  submittedByUserId: string;
  submittedAt: string;
  rowsProcessed?: number;
  errorCount: number;
}

export interface SubmissionDetail extends SubmissionSummary {
  operationId?: string;
  httpStatusCode?: number;
  casfogErrorCount: number;
  casfogErrors?: string[];
  closedAt?: string;
  s3Path: string;
  downloadUrl?: string;
}

export interface PeriodStatus {
  annexId: number;
  year: number;
  month: number;
  hasSuccess: boolean;
  hasPending: boolean;
  requiresAmendment: boolean;
  canSubmit: boolean;
}

export interface CasfogUploadResponse {
  submissionId: number;
  operationId?: string;
  status: string;
  message: string;
  rowsProcessed: number;
  errors?: string[];
}

export interface SubmissionsPage {
  content: SubmissionSummary[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface CreateAnnexRequest {
  name: string;
  requires_year: boolean;
  requires_month: boolean;
  query: string;
}

export interface AnnexesResponse {
  content: AnnexItem[];
  page: number;
  size: number;
  total_elements: number;
  total_pages: number;
}

export const getAnnexes = async (
  page: number = 0,
  size: number = 10,
  searchText: string = ''
) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (searchText.trim()) {
      params.append('filterText', searchText.trim());
    }

    const url = `${apiUrls.annexes}?${params.toString()}`;
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching annexes:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getAnnex = async (id: string) => {
  try {
    const url = `${apiUrls.annexes}/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching annex:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const createAnnex = async (annexData: CreateAnnexRequest) => {
  try {
    return securePost(apiUrls.annexes, annexData);
  } catch (error) {
    console.error('Error creating annex:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const updateAnnex = async (id: string, annexData: CreateAnnexRequest) => {
  try {
    const url = `${apiUrls.annexes}/${id}`;
    return securePut(url, annexData);
  } catch (error) {
    console.error('Error updating annex:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const executeAnnex = async (id: number, year?: number, month?: number, annexName?: string) => {
  try {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    
    const url = `${apiUrls.annexes}/${id}/execute${params.toString() ? '?' + params.toString() : ''}`;
    
    // Para descarga de CSV, usamos fetch directamente para manejar la respuesta como blob
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentDisposition = response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const yyyymmdd = `${yyyy}${mm}${dd}`;
    const sanitizedAnnexName = (annexName || `ANNEX_${id}`)
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .toUpperCase();
    let suggestedFileName = `${sanitizedAnnexName}_${yyyymmdd}.csv`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";]+)"?/i);
      if (match && match[1]) {
        suggestedFileName = match[1];
      }
    }

    const blob = await response.blob();
    
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = suggestedFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true };
  } catch (error) {
    console.error('Error executing annex:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const uploadAnnexToCasfog = async (
  annexId: number,
  file: File,
  year: number,
  month: number,
  userId: string,
  submissionType?: string
): Promise<CasfogUploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const params = new URLSearchParams({
      year: year.toString(),
      month: month.toString(),
      userId: userId,
    });
    if (submissionType) {
      params.append('submissionType', submissionType);
    }
    
    const url = `${apiUrls.annexes}/${annexId}/casfog-upload?${params.toString()}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error uploading annex to CASFOG:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getCasfogSubmissions = async (
  annexIds?: number[],
  year?: number,
  month?: number,
  status?: string,
  userId?: string,
  page: number = 0,
  size: number = 20
): Promise<SubmissionsPage> => {
  try {
    const params = new URLSearchParams();
    if (annexIds?.length) {
      params.append('annexIds', annexIds.join(','));
    }
    if (year !== undefined) {
      params.append('year', year.toString());
    }
    if (month !== undefined) {
      params.append('month', month.toString());
    }
    if (status) {
      params.append('status', status);
    }
    if (userId) {
      params.append('userId', userId);
    }
    params.append('page', page.toString());
    params.append('size', size.toString());
    
    const url = `${apiUrls.annexes}/submissions?${params.toString()}`;
    const response = await secureGet(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching CASFOG submissions:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getCasfogSubmissionDetail = async (id: number): Promise<SubmissionDetail> => {
  try {
    const url = `${apiUrls.annexes}/submissions/${id}`;
    const response = await secureGet(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching submission detail:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getSubmissionStatusForPeriod = async (
  annexId: number,
  year: number,
  month: number
): Promise<PeriodStatus> => {
  try {
    const url = `${apiUrls.annexes}/${annexId}/status-for-period?year=${year}&month=${month}`;
    const response = await secureGet(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching period status:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getLatestSubmission = async (annexId: number): Promise<SubmissionSummary | null> => {
  try {
    const url = `${apiUrls.annexes}/${annexId}/latest-submission`;
    const response = await secureGet(url);
    return response.data || null;
  } catch (error) {
    console.error('Error fetching latest submission:', JSON.stringify(error, null, 2));
    return null;
  }
};